import {
	IconHouse,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@exopengithub/exokit";
import { SidebarToggleButton } from "@exopengithub/exokit/v2";
import {
	IconArrowNarrowRight,
	IconCirclePlus,
	IconDatabase,
} from "@tabler/icons-react";
import { clsx } from "clsx";
import { EntityPickerField } from "molecules/entity-picker/EntityPickerField";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { AddCompany } from "src/features/companyAdministration/AddCompany.js";
import { useUser } from "src/common/context/userContextUtils.js";
import { useLastActiveContext } from "src/common/utils/hooks/useLastActiveContext.js";
import { useLoadedCompanyDomains } from "src/routes/routeDataHooks.js";
import { NavItem } from "./NavItem";
import { SidebarContext } from "./SidebarContext.js";
import { useDevToolsUnlocked } from "./useDevToolsUnlock";
import { useLanguageToggle } from "./useLanguageToggle.js";
import { ALL_LEGAL_ENTITIES } from "src/common/utils/lastActiveContext.js";
import { DOMAIN_TERMS } from "src/common/domainTerms.js";

interface AllDomainsSidebarPartProps {
	sidebarOpen: boolean;
}

/** Same chrome as `CompanySidebarPart`, but with no single company domain to anchor to. */
export const AllDomainsSidebarPart = ({
	sidebarOpen,
}: AllDomainsSidebarPartProps) => {
	const { t } = useTranslation();
	const user = useUser();
	const companyDomains = useLoadedCompanyDomains();
	const { write: writeLastActiveContext } = useLastActiveContext();
	const { toggleSidebar } = useContext(SidebarContext);
	const isSuperAdmin = user.userIsSuperAdmin();
	const devToolsUnlocked = useDevToolsUnlocked(isSuperAdmin);
	useLanguageToggle();

	const items = useMemo(() => {
		return companyDomains
			.map((companyDomain) => ({
				id: `${companyDomain.id}`,
				name: companyDomain.name,
			}))
			.toSorted((a, b) => a.name.localeCompare(b.name));
	}, [companyDomains]);

	return (
		<div className="grow overflow-y-auto">
			{/* WHY always rendered, invisible when collapsed: reserves the exact
			 * same height as open (border/p-3/h-9 pickers), so every icon below
			 * stays at the same y regardless of open/collapsed. */}
			<div
				className={clsx(
					// WHY: same Figma 9833:113707 spacing as CompanySidebarPart.
					"mx-4 mb-2 flex flex-col gap-1 border border-[#00A329]/20 p-3",
					!sidebarOpen && "invisible",
				)}
			>
				<div className="rounded-xs bg-white">
					{sidebarOpen ? (
						<EntityPickerField
							pillLabel={t(DOMAIN_TERMS.DOMAIN)}
							currentLabel={t("All domains")}
							items={items}
							searchPlaceholder={t("Search company domain")}
							renderHeaderAction={({ close }) =>
								isSuperAdmin && (
									<AddCompany onCreated={close}>
										{(triggerProps) => (
											<Tooltip placement="top">
												<TooltipTrigger asChild>
													<button
														type="button"
														{...triggerProps}
														aria-label={t("New Company Domain")}
														className="shrink-0 rounded-full p-1 text-primary-800 hover:bg-gray-100"
													>
														<IconCirclePlus size={20} strokeWidth={1.5} />
													</button>
												</TooltipTrigger>
												<TooltipContent>
													{t("New Company Domain")}
												</TooltipContent>
											</Tooltip>
										)}
									</AddCompany>
								)
							}
							renderItem={(companyDomain, { close }) => (
								<Link
									to={`/company-domains/${companyDomain.id}`}
									className="group flex items-center gap-2 border-b px-3 py-2 text-gray-700 last:border-b-0 hover:bg-gray-100"
									onClick={() => {
										writeLastActiveContext({
											companyDomainId: companyDomain.id,
											legalEntityId: ALL_LEGAL_ENTITIES,
										});
										close();
									}}
								>
									<div className="grow truncate">{companyDomain.name}</div>
									<IconArrowNarrowRight
										className="shrink-0 text-gray-500 opacity-0 group-hover:opacity-100"
										size={16}
										strokeWidth={1.5}
									/>
								</Link>
							)}
						/>
					) : (
						<div className="h-9" />
					)}
				</div>
				{/* Real cross-domain search would mean one request per domain — static for now. */}
				<div className="rounded-xs bg-white">
					{sidebarOpen ? (
						<EntityPickerField
							pillLabel={t(DOMAIN_TERMS.COMPANY)}
							currentLabel={t("All companies")}
							items={[]}
							searchPlaceholder={t("Search legal entities")}
							renderItem={() => null}
						/>
					) : (
						<div className="h-9" />
					)}
				</div>
			</div>
			<div
				className={clsx(
					"relative mx-4 mb-1 flex items-center justify-between p-3",
					!sidebarOpen && "invisible",
				)}
			>
				<span className="text-xs font-medium text-[#00661A]/60">
					{t("Menu")}
				</span>
				{sidebarOpen && (
					<SidebarToggleButton
						onClick={toggleSidebar}
						label={t("Collapse sidebar")}
						iconClassName="text-primary-800"
					/>
				)}
				{/* WHY visible: overrides the row's own invisible above — the
				 * toggle takes this exact spot instead of adding a row of its
				 * own, so it never moves between open and collapsed. */}
				{!sidebarOpen && (
					<div className="visible absolute inset-0 flex items-center justify-center">
						<SidebarToggleButton
							onClick={toggleSidebar}
							label={t("Expand sidebar")}
							iconClassName="text-primary-800"
							className="rounded-xs p-3 hover:bg-white/60"
						/>
					</div>
				)}
			</div>
			<nav>
				<div className="mx-4 mb-4 space-y-1">
					<NavItem to="/" label="Start" icon={<IconHouse />} end />
					{devToolsUnlocked && companyDomains[0] && (
						<NavItem
							label={t(DOMAIN_TERMS.DATA_SOURCES)}
							to={`/company-domains/${companyDomains[0].id}/internal/dev-tools/integration-status`}
							icon={<IconDatabase />}
						/>
					)}
				</div>
			</nav>
		</div>
	);
};
